import {useSyncExternalStore} from 'react'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import type {ActionabilityCategory, ActionabilityState} from './actionability/actionability-types'
import styles from './ActionabilityBanner.module.css'
import {useActionability} from './actionability/use-actionability'
import {pullRequestsLayoutRoute} from '../../routes/layout-route'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {MergeMethodContextProvider} from '@github-ui/mergebox'
import type {Channels} from '@github-ui/mergebox/use-mergeability-live-updates'
import type {NavigationUrls} from '../../types/navigation-urls-types'

const CATEGORY_LABELS: Record<ActionabilityCategory, string> = {
  review: 'Your turn',
  fix: 'Action needed',
  ready: 'Ready to merge',
  merging: 'In merge queue',
  draft: 'Work in progress',
  waiting: 'No action needed',
}

const LABEL_CLASS: Record<ActionabilityCategory, string> = {
  review: styles.labelReview,
  fix: styles.labelFix,
  ready: styles.labelReady,
  merging: styles.labelMerging,
  draft: styles.labelDraft,
  waiting: styles.labelWaiting,
}

const LOADING_PHRASES = [
  'Traversing the commit graph…',
  'Reticulating CI pipelines…',
  "Deserializing your pull request's destiny…",
  'git blame-ing the delay…',
  'Garbage collecting stale opinions…',
  'Checking if the dog ate your approvals…',
  "Waiting for Schrödinger's CI to collapse into a state…",
  'Begging the automerge daemon for a straight answer…',
  'Asking git log --oneline for the meaning of life…',
  'Checking out your fate on a detached HEAD…',
]

/** Handler map for action-type CTAs. Wire up real implementations here. */
type ActionHandlers = Record<string, () => void>

interface ActionabilityBannerProps {
  actionability: ActionabilityState
  urls: NavigationUrls
  /** Optional handlers for action-type CTAs (e.g. open_mergebox, mark_ready_for_review) */
  onAction?: ActionHandlers
}

export function ActionabilityBannerWrapper() {
  const {data: layoutData} = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const viewerIsRequestedReviewer = layoutData.bannersData.banners.reviewRequestBanner.render

  if (layoutData.mergeStatusButtonData == null) {
    return null
  }

  return (
    <MergeMethodContextProvider defaultMergeMethod={layoutData.mergeStatusButtonData.defaultMergeMethod}>
      <ActionabilityBanner
        authorLogin={layoutData.pullRequest.author.login}
        urls={layoutData.urls}
        headSha={layoutData.mergeStatusButtonData.headSha}
        channels={layoutData.mergeStatusButtonData.channels}
        viewerIsRequestedReviewer={viewerIsRequestedReviewer}
      />
    </MergeMethodContextProvider>
  )
}

export function ActionabilityBanner({
  authorLogin,
  urls,
  headSha,
  channels,
  viewerIsRequestedReviewer,
}: {
  authorLogin: string
  urls: NavigationUrls
  headSha: string
  channels: Channels | null
  viewerIsRequestedReviewer: boolean
}) {
  const {actionability, isLoading} = useActionability({
    authorLogin,
    headSha,
    channels,
    viewerIsRequestedReviewer,
  })

  if (isLoading) {
    return <ActionabilityBannerSkeleton />
  }

  if (!actionability) return null

  return <ActionabilityBannerComponent actionability={actionability} urls={urls} />
}

export function ActionabilityBannerComponent({actionability, urls, onAction}: ActionabilityBannerProps) {
  const {category, message, actionLabel, actionTarget} = actionability

  return (
    <div className={clsx(styles.banner, styles[category])} data-testid="actionability-banner">
      <p className={styles.text}>
        <span className={clsx(styles.label, LABEL_CLASS[category])}>{CATEGORY_LABELS[category]}</span> {message}
      </p>
      {actionTarget.type === 'link' ? (
        <Button as="a" href={urls[actionTarget.urlKey]} className={styles.actionButton}>
          {actionLabel}
        </Button>
      ) : (
        <Button className={styles.actionButton} onClick={() => onAction?.[actionTarget.actionId]?.()}>
          {actionLabel}
        </Button>
      )}
    </div>
  )
}

const noopSubscribe = () => () => {}
const getLoadingPhrase = () => LOADING_PHRASES[new Date().getMinutes() % LOADING_PHRASES.length]
const getServerLoadingPhrase = () => LOADING_PHRASES[0]

function ActionabilityBannerSkeleton() {
  const phrase = useSyncExternalStore(noopSubscribe, getLoadingPhrase, getServerLoadingPhrase)

  return (
    <div className={clsx(styles.banner, styles.loading)} data-testid="actionability-banner-loading">
      <p className={styles.loadingText}>{phrase}</p>
      <LoadingSkeleton variant="rounded" height="sm" width="40%" />
      <LoadingSkeleton variant="rounded" height="sm" width="100%" className={styles.loadingButton} />
    </div>
  )
}
